import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, of, tap } from 'rxjs';
import { environment } from '@app-environment';
import { ApiCacheService } from './api-cache.service';

export interface EntityDefinitionAttribute {
  name?: string;
  displayName?: string | null;
  businessKey?: boolean;
  systemAttributes?: boolean;
  type?: string;
  validValues?: unknown[] | null;
  [key: string]: unknown;
}

export interface EntityDefinition {
  entityName?: string;
  displayName?: string;
  description?: string;
  category?: string;
  type?: string;
  attributes?: Record<string, EntityDefinitionAttribute>;
  childEntities?: EntityDefinition[];
  [key: string]: unknown;
}

export type EntityDefinitionsResponse = Record<string, EntityDefinition> | {
  data?: Record<string, EntityDefinition>;
  entityDefinitions?: Record<string, EntityDefinition>;
  [key: string]: unknown;
};

export interface NamedEntityDefinition {
  name: string;
  definition: EntityDefinition;
}

/** Loads the entity catalog once and shares it across the application. */
@Injectable({ providedIn: 'root' })
export class EntityDefinitionsService {
  private readonly url = `${environment.URL.replace(/\/$/, '')}/spriced/platform/getAllEntityDefinitions`;
  private readonly cacheKey = ApiCacheService.key('entity-definitions', this.url);

  constructor(
    private readonly http: HttpClient,
    private readonly cache: ApiCacheService,
  ) {}

  load(): Observable<EntityDefinitionsResponse> {
    const cached = this.cache.get<EntityDefinitionsResponse>(this.cacheKey);
    if (cached) return of(cached);

    return this.cache.getOrLoad(
      this.cacheKey,
      () => this.http.get<EntityDefinitionsResponse>(this.url).pipe(
        tap((response) => this.cache.set(this.cacheKey, response)),
      ),
    );
  }

  extract(response: EntityDefinitionsResponse): NamedEntityDefinition[] {
    const wrapped = response as any;
    const source = wrapped?.entityDefinitions ?? wrapped?.data ?? response;
    const entries: Array<[string, EntityDefinition]> = Array.isArray(source)
      ? source.map((definition: EntityDefinition, index: number) => [
          String(definition?.entityName ?? index),
          definition,
        ])
      : Object.entries(source || {}) as Array<[string, EntityDefinition]>;

    return entries
      .map(([key, definition]) => ({
        name: String(definition?.entityName ?? key).trim(),
        definition,
      }))
      .filter(({ name, definition }) =>
        !!name && !!definition && typeof definition === 'object',
      );
  }

  async preload(): Promise<void> {
    try {
      await firstValueFrom(this.load());
    } catch (error) {
      console.error('[ENTITY_DEFINITIONS_LOAD_FAILED]', error);
    }
  }
}
